import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import MarketingHome from "@/pages/MarketingHome";
import { AuthProvider, useAuth } from "@/credit/AuthContext";
import CreditLanding from "@/credit/pages/CreditLanding";
import Login from "@/credit/pages/Login";
import Signup from "@/credit/pages/Signup";
import Pricing from "@/credit/pages/Pricing";
import ForgotPassword from "@/credit/pages/ForgotPassword";
import ResetPassword from "@/credit/pages/ResetPassword";
import VerifyEmail from "@/credit/pages/VerifyEmail";
import AuthCallback from "@/credit/pages/AuthCallback";
import DashboardLayout from "@/credit/DashboardLayout";
import Dashboard from "@/credit/pages/Dashboard";
import BusinessBuilder from "@/credit/pages/BusinessBuilder";
import BusinessCredit from "@/credit/pages/BusinessCredit";
import Vendors from "@/credit/pages/Vendors";
import PersonalCredit from "@/credit/pages/PersonalCredit";
import ReportAnalyzer from "@/credit/pages/ReportAnalyzer";
import Disputes from "@/credit/pages/Disputes";
import Coach from "@/credit/pages/Coach";
import Vault from "@/credit/pages/Vault";
import Funding from "@/credit/pages/Funding";
import Learning from "@/credit/pages/Learning";
import Tasks from "@/credit/pages/Tasks";
import Analytics from "@/credit/pages/Analytics";
import Billing from "@/credit/pages/Billing";
import Settings from "@/credit/pages/Settings";
import PaymentSuccess from "@/credit/pages/PaymentSuccess";
import PaymentCancel from "@/credit/pages/PaymentCancel";
import AdminApp from "@/credit/admin/AdminApp";
import PrivacyPolicy from "@/pages/legal/PrivacyPolicy";
import TermsOfService from "@/pages/legal/TermsOfService";
import RefundPolicy from "@/pages/legal/RefundPolicy";
import HelpCenter from "@/pages/HelpCenter";
import HelpWidget from "@/components/HelpWidget";

const Protected = ({ children }) => {
  const { user, ready } = useAuth();
  if (!ready) {
    return (
      <div className="cc cc-bg min-h-screen flex items-center justify-center text-white/60">
        Loading…
      </div>
    );
  }
  if (!user) return <Navigate to="/credit/login" replace />;
  return children;
};

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const AppRouter = () => {
  const location = useLocation(); // read hash from here, not window.location.hash (not reactive)
  // Emergent Google auth callback: session_id arrives in the URL fragment — handle it BEFORE any route/guard runs
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  const hideWidget = location.pathname.startsWith("/admin") || location.pathname === "/help";
  return (
    <>
    <Routes>
      <Route path="/" element={<MarketingHome />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/terms" element={<TermsOfService />} />
      <Route path="/refund-policy" element={<RefundPolicy />} />
      <Route path="/help" element={<HelpCenter />} />
      <Route path="/credit" element={<CreditLanding />} />
      <Route path="/credit/login" element={<Login />} />
      <Route path="/credit/signup" element={<Signup />} />
      <Route path="/credit/pricing" element={<Pricing />} />
      <Route path="/credit/forgot-password" element={<ForgotPassword />} />
      <Route path="/credit/reset-password" element={<ResetPassword />} />
      <Route path="/credit/verify-email" element={<VerifyEmail />} />
      <Route path="/payment/success" element={<Protected><PaymentSuccess /></Protected>} />
      <Route path="/payment/cancel" element={<Protected><PaymentCancel /></Protected>} />
      <Route path="/admin/*" element={<AdminApp />} />
      <Route
        path="/credit/app"
        element={
          <Protected>
            <DashboardLayout />
          </Protected>
        }
      >
        <Route index element={<Dashboard />} />
        <Route path="business-builder" element={<BusinessBuilder />} />
        <Route path="business-credit" element={<BusinessCredit />} />
        <Route path="vendors" element={<Vendors />} />
        <Route path="personal-credit" element={<PersonalCredit />} />
        <Route path="report-analyzer" element={<ReportAnalyzer />} />
        <Route path="disputes" element={<Disputes />} />
        <Route path="coach" element={<Coach />} />
        <Route path="vault" element={<Vault />} />
        <Route path="funding" element={<Funding />} />
        <Route path="learning" element={<Learning />} />
        <Route path="tasks" element={<Tasks />} />
        <Route path="analytics" element={<Analytics />} />
        <Route path="billing" element={<Billing />} />
        <Route path="settings" element={<Settings />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    {!hideWidget && <HelpWidget />}
    </>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <Toaster theme="dark" position="top-right" richColors />
          <AppRouter />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
